// Google Analytics 4 Service
// Professional implementation with TypeScript support

export interface GAEvent {
  action: string;
  category: string;
  label?: string;
  value?: number;
}

export interface GAEcommerceItem {
  item_id: string;
  item_name: string;
  category: string;
  quantity: number;
  price: number;
}

export interface GAEcommerceEvent {
  transaction_id: string;
  value: number;
  currency: string;
  items: GAEcommerceItem[];
}

// Optional event-level metadata for lists and checkout
export interface GAListMetadata {
  item_list_id?: string;
  item_list_name?: string;
}

export interface GACheckoutMetadata {
  value?: number;
  currency?: string;
  coupon?: string;
  payment_type?: string;
}

export interface GAPageView {
  page_title: string;
  page_location: string;
  page_path: string;
}

export interface GAConfig {
  page_title?: string;
  page_location?: string;
  debug_mode?: boolean;
  send_page_view?: boolean;
  anonymize_ip?: boolean;
  allow_google_signals?: boolean;
  client_id?: string;
  custom_parameter_1?: string;
  custom_parameter_2?: string;
  linker?: {
    domains: string[];
  };
  user_properties?: {
    [key: string]: string;
  };
  [key: string]: string | number | boolean | object | undefined;
}

export interface GAUserConfig {
  user_id: string;
  custom_parameter_1?: string;
  custom_parameter_2?: string;
  [key: string]: string | number | boolean | undefined;
}

export interface GAPageViewData {
  page_title: string;
  page_location: string;
  page_path: string;
  user_id?: string | null;
  session_id?: string | null;
  client_id?: string | null;
  [key: string]: string | number | boolean | null | undefined;
}

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag: (
      command: 'config' | 'event' | 'js' | 'set' | 'consent',
      targetId: string | Date | object,
      config?: object,
    ) => void;
    dataLayer: unknown[];
    analytics?: {
      trackEvent: (
        eventName: string,
        parameters?: Record<string, string | number | boolean>,
      ) => void;
      trackPageView: (pagePath?: string, pageTitle?: string) => void;
      setUserId: (userId: string) => void;
      setUserProperties: (properties: Record<string, string | number | boolean>) => void;
    };
  }
}

class GoogleAnalyticsService {
  private measurementId: string | null = null;
  private isInitialized = false;
  private isEnabled = true;
  private debugMode = false;
  private initPromise: Promise<void> | null = null;
  private idleInitializationPromise: Promise<void> | null = null;
  private eventQueue: Array<() => void> = [];
  private isConsentGiven = false;
  private recentTransactionIds: Set<string> = new Set();

  constructor() {
    this.measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
    this.debugMode = import.meta.env.DEV || false;

    // Check for stored consent
    this.isConsentGiven = localStorage.getItem('ga-consent') === 'granted';

    // Auto-grant consent in both development and production if not explicitly denied
    if (!localStorage.getItem('ga-consent')) {
      this.isConsentGiven = true;
      localStorage.setItem('ga-consent', 'granted');
    }

    // Production-specific optimizations
    if (import.meta.env.PROD) {
      // Enable enhanced measurement in production
      this.isEnabled = true;
    }
  }

  /**
   * Initialize Google Analytics with lazy loading
   */
  async initialize(): Promise<void> {
    if (!this.measurementId) {
      console.warn('Google Analytics: Measurement ID not found');
      return;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    if (this.isInitialized) {
      return;
    }

    // Only initialize if consent is given
    if (!this.isConsentGiven) {
      if (this.debugMode) {
        console.log('Google Analytics: Waiting for user consent');
      }
      return;
    }

    this.initPromise = (async () => {
      await this.waitForIdleInitializationWindow();
      await this.performInitialization();
    })();
    return this.initPromise;
  }

  private waitForIdleInitializationWindow(): Promise<void> {
    if (typeof window === 'undefined') {
      return Promise.resolve();
    }

    if (this.idleInitializationPromise) {
      return this.idleInitializationPromise;
    }

    this.idleInitializationPromise = new Promise((resolve) => {
      const scheduleWhenIdle = () => {
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback?.(() => resolve(), { timeout: 10000 });
        } else {
          window.setTimeout(resolve, 5000);
        }
      };

      if (document.readyState === 'complete') {
        scheduleWhenIdle();
        return;
      }

      window.addEventListener('load', scheduleWhenIdle, { once: true });
    });

    return this.idleInitializationPromise;
  }

  /**
   * Perform the actual initialization
   */
  private async performInitialization(): Promise<void> {
    try {
      if (this.debugMode) {
        console.log('Starting Google Analytics initialization...');
        console.log('Measurement ID:', this.measurementId);
        console.log('Consent given:', this.isConsentGiven);
      }

      // Initialize dataLayer
      window.dataLayer = window.dataLayer || [];
      if (this.debugMode) {
        console.log('DataLayer initialized:', !!window.dataLayer);
      }

      // Define gtag function with enhanced debugging
      window.gtag = function (...args) {
        if (import.meta.env.DEV) {
          console.log('🔧 Gtag called with:', args);
          console.log('🔧 DataLayer before push:', window.dataLayer.length);
        }
        window.dataLayer.push(args);
        if (import.meta.env.DEV) {
          console.log('🔧 DataLayer after push:', window.dataLayer.length);
          // Log the actual dataLayer content for debugging
          console.log('🔧 Latest dataLayer entry:', window.dataLayer[window.dataLayer.length - 1]);
        }
      };
      if (this.debugMode) {
        console.log('Gtag function defined');
      }

      // Set initial timestamp
      window.gtag('js', new Date());

      // 2) Consent defaults (must be before any 'config')
      window.gtag('consent', 'default', {
        ad_storage: 'denied',
        analytics_storage: 'granted',
        functionality_storage: 'granted',
        security_storage: 'granted',
      });

      // Load GA4 script with lazy loading
      if (this.debugMode) {
        console.log('Loading GA4 script...');
      }
      await this.loadGAScript();
      if (this.debugMode) {
        console.log('GA4 script loaded successfully');
      }

      // Generate or retrieve client ID for better user tracking
      const clientId = this.getOrCreateClientId();
      if (this.debugMode) {
        console.log('Client ID:', clientId);
      }

      // 3) Build GA config (⚠️ remove client_id here)
      const gaConfig: GAConfig = {
        page_title: document.title,
        page_location: window.location.href,
        debug_mode: this.debugMode,
        send_page_view: false,
        anonymize_ip: true,
        allow_google_signals: this.isConsentGiven,
        allow_ad_personalization_signals: this.isConsentGiven,
        client_id: clientId, // Ensure unique client identification
        cookie_flags: 'SameSite=None;Secure', // Better cross-domain tracking
      };

      // Production add-ons (safe)
      if (import.meta.env.PROD) {
        gaConfig.enhanced_conversions = true;
        gaConfig.linker = {
          domains: ['eztopup.com.mm', 'myeztopup.com', 'core.myeztopup.com'],
        };
        gaConfig.custom_map = {
          custom_parameter_1: 'user_type',
          custom_parameter_2: 'session_id',
        };
      }

      void console.log('GA Config:', gaConfig);
      window.gtag('config', this.measurementId!, gaConfig);

      if (this.debugMode) {
        console.log('✅ GA4 configured with measurement ID:', this.measurementId);

        // Wait a moment then check if GA is actually ready to send events
        setTimeout(() => {
          console.log('🔍 Checking GA readiness after config...');
          console.log('🔍 DataLayer current state:', window.dataLayer);
          console.log('🔍 Gtag function type:', typeof window.gtag);

          // GA is ready for use
        }, 500);
      }

      this.isInitialized = true;

      // Process queued events
      this.processEventQueue();
      if (this.debugMode) {
        console.log('Event queue processed');
        console.log('Google Analytics initialized successfully');
      }
    } catch (error) {
      console.error('Failed to initialize Google Analytics:', error);
      this.initPromise = null; // Reset to allow retry
    }
  }

  /**
   * Generate or retrieve client ID for unique user identification
   */
  private getOrCreateClientId(): string {
    const storageKey = 'ga_client_id';
    let clientId = localStorage.getItem(storageKey);

    if (!clientId) {
      // Generate a unique client ID
      clientId =
        'GA1.1.' +
        Math.random().toString(36).substring(2, 15) +
        '.' +
        Math.floor(Date.now() / 1000);
      localStorage.setItem(storageKey, clientId);
    }

    return clientId;
  }

  /**
   * Load Google Analytics script with performance optimization
   */
  private loadGAScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.measurementId) {
        const error = new Error('Measurement ID not available');
        console.error('GA Script Load Error:', error.message);
        reject(error);
        return;
      }

      // Check if script is already loaded
      const existingScript = document.querySelector(
        `script[src*="gtag/js?id=${this.measurementId}"]`,
      );
      if (existingScript) {
        if (this.debugMode) {
          console.log('GA script already loaded');
        }
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.async = true;
      script.defer = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;

      script.onload = () => {
        if (this.debugMode) {
          console.log('GA script loaded successfully from:', script.src);
          console.log('Window.gtag available:', typeof window.gtag);
          console.log('DataLayer length:', window.dataLayer?.length || 0);
          console.log('DataLayer contents:', window.dataLayer);

          // Wait a bit for gtag to be fully ready
          setTimeout(() => {
            // GA script is ready for use
          }, 100);
        }
        // Store successful load timestamp for debugging
        localStorage.setItem('ga-script-loaded', new Date().toISOString());
        // Monitor network requests after script load
        this.monitorNetworkRequests();
        resolve();
      };

      script.onerror = (error) => {
        const errorMsg = `Failed to load GA script from ${script.src}`;
        console.error(errorMsg, error);
        console.error('Network status:', navigator.onLine ? 'online' : 'offline');
        // Store error for debugging
        localStorage.setItem(
          'ga-script-error',
          JSON.stringify({
            timestamp: new Date().toISOString(),
            error: errorMsg,
            online: navigator.onLine,
          }),
        );
        reject(new Error(errorMsg));
      };

      if (this.debugMode) {
        console.log('Loading GA script from:', script.src);
      }
      document.head.appendChild(script);
    });
  }

  /**
   * Monitor network requests for debugging
   */
  private monitorNetworkRequests(): void {
    if (!this.debugMode) return;

    console.log('🔍 Starting network monitoring for GA requests...');

    // Monitor fetch requests
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const [resource] = args;
      let url: string;

      if (typeof resource === 'string') {
        url = resource;
      } else if (resource instanceof Request) {
        url = resource.url;
      } else if (resource instanceof URL) {
        url = resource.toString();
      } else {
        url = String(resource);
      }

      // Check for GA-related requests with more specific patterns
      const isGARequest =
        url.includes('google-analytics.com') ||
        url.includes('googletagmanager.com') ||
        url.includes('analytics.google.com') ||
        url.includes('/collect') ||
        url.includes('/g/collect') ||
        url.includes('www.google-analytics.com');

      if (isGARequest) {
        console.log('🚀 GA Network Request detected!');
        console.log('🚀 Full URL:', url);
        console.log('🚀 Request method:', args[1]?.method || 'GET');
        console.log('🚀 Request headers:', args[1]?.headers);
        console.log('🚀 Request body:', args[1]?.body);

        // Check if this is a collect request specifically
        if (url.includes('/collect') || url.includes('/g/collect')) {
          console.log('🎯 This is a GA COLLECT request - data is being sent!');
        }

        try {
          const response = await originalFetch(...args);
          console.log('✅ GA Network Response:', response.status, response.statusText);
          console.log('✅ Response headers:', response.headers);
          return response;
        } catch (error) {
          console.error('❌ GA Network Error:', error);
          throw error;
        }
      }

      return originalFetch(...args);
    };

    // Monitor XMLHttpRequest
    const originalXHROpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (
      method: string,
      url: string | URL,
      async?: boolean,
      username?: string | null,
      password?: string | null,
    ) {
      const urlString = typeof url === 'string' ? url : url.toString();

      if (
        urlString.includes('google-analytics.com') ||
        urlString.includes('googletagmanager.com')
      ) {
        void console.log('GA XHR Request:', method, urlString);

        this.addEventListener('load', () => {
          void console.log('GA XHR Response:', this.status, this.statusText);
        });

        this.addEventListener('error', () => {
          console.error('GA XHR Error:', this.status, this.statusText);
        });
      }

      return originalXHROpen.call(this, method, url, async ?? true, username, password);
    };

    // Monitor sendBeacon
    const originalSendBeacon = navigator.sendBeacon?.bind(navigator);
    if (originalSendBeacon) {
      navigator.sendBeacon = function (url: string | URL, data?: BodyInit | null): boolean {
        const urlString = typeof url === 'string' ? url : url.toString();
        if (
          urlString.includes('google-analytics.com') ||
          urlString.includes('googletagmanager.com') ||
          urlString.includes('/collect') ||
          urlString.includes('/g/collect')
        ) {
          console.log('🛰️ GA Beacon Request:', urlString);
          console.log('🛰️ Beacon data:', data);
        }
        return originalSendBeacon(url, data ?? null);
      };
    }
  }

  /**
   * Process queued events after initialization
   */
  private processEventQueue(): void {
    while (this.eventQueue.length > 0) {
      const eventFn = this.eventQueue.shift();
      if (eventFn) {
        try {
          eventFn();
        } catch (error) {
          console.error('Error processing queued analytics event:', error);
        }
      }
    }
  }

  /**
   * Grant user consent for analytics
   */
  grantConsent(): void {
    this.isConsentGiven = true;
    localStorage.setItem('ga-consent', 'granted');

    if (this.debugMode) {
      console.log('Google Analytics: Consent granted');
    }

    // Initialize if not already done
    if (!this.isInitialized && !this.initPromise) {
      this.initialize();
    }
  }

  /**
   * Revoke user consent for analytics
   */
  revokeConsent(): void {
    this.isConsentGiven = false;
    localStorage.setItem('ga-consent', 'denied');

    if (this.debugMode) {
      console.log('Google Analytics: Consent revoked');
    }
  }

  /**
   * Check if user has given consent
   */
  hasConsent(): boolean {
    return this.isConsentGiven;
  }

  /**
   * Track page view with queuing support
   */
  trackPageView(pageData: Partial<GAPageView> = {}): void {
    if (!this.isEnabled) return;

    const trackEvent = () => {
      try {
        const pageView: GAPageViewData = {
          page_title: document.title,
          page_location: window.location.href,
          page_path: window.location.pathname,
          ...pageData,
        };

        // Production-specific page view enhancements
        if (import.meta.env.PROD) {
          pageView.send_to = this.measurementId;
          pageView.user_id = localStorage.getItem('ga_user_id');
          pageView.session_id = sessionStorage.getItem('ga_session_id');
          pageView.engagement_time_msec = 100;

          // Add custom dimensions for better tracking
          pageView.custom_parameter_1 = 'production_user';
          pageView.custom_parameter_2 = pageView.session_id;
        }

        window.gtag('event', 'page_view', pageView);

        if (this.debugMode) {
          console.log('GA Page View:', pageView);
        }
      } catch (error) {
        console.error('Failed to track page view:', error);
      }
    };

    if (!this.isInitialized) {
      if (this.isConsentGiven) {
        this.eventQueue.push(trackEvent);
        // Try to initialize if consent is given
        this.initialize();
      }
      return;
    }

    trackEvent();
  }

  /**
   * Track custom event with queuing support
   */
  trackEvent(event: GAEvent): void {
    if (!this.isEnabled) return;

    const trackEventFn = () => {
      try {
        window.gtag('event', event.action, {
          event_category: event.category,
          event_label: event.label,
          value: event.value,
        });

        if (this.debugMode) {
          console.log('GA Event:', event);
        }
      } catch (error) {
        console.error('Failed to track event:', error);
      }
    };

    if (!this.isInitialized) {
      if (this.isConsentGiven) {
        this.eventQueue.push(trackEventFn);
        this.initialize();
      }
      return;
    }

    trackEventFn();
  }

  /**
   * Track e-commerce purchase with queuing support
   */
  trackPurchase(purchaseData: GAEcommerceEvent): void {
    if (!this.isEnabled || !this.isConsentGiven) {
      if (this.debugMode) {
        console.log('Purchase tracking disabled or consent not given');
      }
      return;
    }

    const trackPurchaseFn = () => {
      try {
        // Dedup purchase by transaction_id within session
        if (this.recentTransactionIds.has(purchaseData.transaction_id)) {
          if (this.debugMode) {
            console.log('Skipping duplicate purchase transaction_id:', purchaseData.transaction_id);
          }
          return;
        }
        this.recentTransactionIds.add(purchaseData.transaction_id);
        if (this.debugMode) {
          console.log('Tracking purchase:', purchaseData);
        }

        // Enhanced purchase tracking with additional parameters
        window.gtag('event', 'purchase', {
          transaction_id: purchaseData.transaction_id,
          value: purchaseData.value,
          currency: purchaseData.currency,
          // Ensure delivery even during navigation
          transport_type: 'beacon',
          // Explicitly send to current GA4 property
          send_to: this.measurementId,
          // Enable GA4 DebugView for this event
          debug_mode: this.debugMode,
          // Add timestamp for better tracking
          custom_parameter_1: new Date().toISOString(),
          // Add page location
          page_location: window.location.href,
          page_title: document.title,
          items: purchaseData.items.map((item) => ({
            item_id: item.item_id,
            item_name: item.item_name,
            category: item.category,
            quantity: item.quantity,
            price: item.price,
          })),
        });

        // Also send a conversion event for better tracking
        window.gtag('event', 'conversion', {
          send_to: this.measurementId,
          transaction_id: purchaseData.transaction_id,
          value: purchaseData.value,
          currency: purchaseData.currency,
        });

        if (this.debugMode) {
          console.log('Purchase event sent to GA');
        }
      } catch (error) {
        console.error('Failed to track purchase:', error);
      }
    };

    if (!this.isInitialized) {
      if (this.isConsentGiven) {
        this.eventQueue.push(trackPurchaseFn);
        this.initialize();
        if (this.debugMode) {
          console.log('Purchase event queued for later');
        }
      }
      return;
    }

    trackPurchaseFn();
  }

  /**
   * Track add to cart event with queuing support
   */
  trackAddToCart(item: GAEcommerceItem): void {
    if (!this.isEnabled) return;

    const trackAddToCartFn = () => {
      try {
        window.gtag('event', 'add_to_cart', {
          currency: 'MMK',
          value: item.price * item.quantity,
          items: [item],
        });

        if (this.debugMode) {
          console.log('GA Add to Cart:', item);
        }
      } catch (error) {
        console.error('Failed to track add to cart:', error);
      }
    };

    if (!this.isInitialized) {
      if (this.isConsentGiven) {
        this.eventQueue.push(trackAddToCartFn);
        this.initialize();
      }
      return;
    }

    trackAddToCartFn();
  }

  /**
   * Track remove from cart event with queuing support
   */
  trackRemoveFromCart(item: GAEcommerceItem): void {
    if (!this.isEnabled) return;

    const trackRemoveFromCartFn = () => {
      try {
        window.gtag('event', 'remove_from_cart', {
          currency: 'MMK',
          value: item.price * item.quantity,
          items: [item],
        });

        if (this.debugMode) {
          console.log('GA Remove from Cart:', item);
        }
      } catch (error) {
        console.error('Failed to track remove from cart:', error);
      }
    };

    if (!this.isInitialized) {
      if (this.isConsentGiven) {
        this.eventQueue.push(trackRemoveFromCartFn);
        this.initialize();
      }
      return;
    }

    trackRemoveFromCartFn();
  }

  /**
   * Track viewing a single item
   */
  trackViewItem(item: GAEcommerceItem, list?: GAListMetadata): void {
    if (!this.isEnabled) return;

    const fn = () => {
      try {
        window.gtag('event', 'view_item', {
          ...list,
          items: [item],
        });

        if (this.debugMode) {
          console.log('GA view_item:', item, list);
        }
      } catch (error) {
        console.error('Failed to track view_item:', error);
      }
    };

    if (!this.isInitialized) {
      if (this.isConsentGiven) {
        this.eventQueue.push(fn);
        this.initialize();
      }
      return;
    }

    fn();
  }

  /**
   * Track viewing a list of items
   */
  trackViewItemList(items: GAEcommerceItem[], list?: GAListMetadata): void {
    if (!this.isEnabled) return;

    const fn = () => {
      try {
        window.gtag('event', 'view_item_list', {
          ...list,
          items,
        });

        if (this.debugMode) {
          console.log('GA view_item_list:', items.length, list);
        }
      } catch (error) {
        console.error('Failed to track view_item_list:', error);
      }
    };

    if (!this.isInitialized) {
      if (this.isConsentGiven) {
        this.eventQueue.push(fn);
        this.initialize();
      }
      return;
    }

    fn();
  }

  /**
   * Track selecting an item from a list
   */
  trackSelectItem(item: GAEcommerceItem, list?: GAListMetadata): void {
    if (!this.isEnabled) return;

    const fn = () => {
      try {
        window.gtag('event', 'select_item', {
          ...list,
          items: [item],
        });

        if (this.debugMode) {
          console.log('GA select_item:', item, list);
        }
      } catch (error) {
        console.error('Failed to track select_item:', error);
      }
    };

    if (!this.isInitialized) {
      if (this.isConsentGiven) {
        this.eventQueue.push(fn);
        this.initialize();
      }
      return;
    }

    fn();
  }

  /**
   * Track viewing the cart
   */
  trackViewCart(items: GAEcommerceItem[], meta?: GACheckoutMetadata): void {
    if (!this.isEnabled) return;

    const fn = () => {
      try {
        window.gtag('event', 'view_cart', {
          value: meta?.value,
          currency: meta?.currency,
          items,
        });

        if (this.debugMode) {
          console.log('GA view_cart:', items.length, meta);
        }
      } catch (error) {
        console.error('Failed to track view_cart:', error);
      }
    };

    if (!this.isInitialized) {
      if (this.isConsentGiven) {
        this.eventQueue.push(fn);
        this.initialize();
      }
      return;
    }

    fn();
  }

  /**
   * Track beginning of checkout
   */
  trackBeginCheckout(items: GAEcommerceItem[], meta?: GACheckoutMetadata): void {
    if (!this.isEnabled) return;

    const fn = () => {
      try {
        window.gtag('event', 'begin_checkout', {
          value: meta?.value,
          currency: meta?.currency,
          coupon: meta?.coupon,
          items,
        });

        if (this.debugMode) {
          console.log('GA begin_checkout:', items.length, meta);
        }
      } catch (error) {
        console.error('Failed to track begin_checkout:', error);
      }
    };

    if (!this.isInitialized) {
      if (this.isConsentGiven) {
        this.eventQueue.push(fn);
        this.initialize();
      }
      return;
    }

    fn();
  }

  /**
   * Track adding payment info during checkout
   */
  trackAddPaymentInfo(items: GAEcommerceItem[], meta?: GACheckoutMetadata): void {
    if (!this.isEnabled) return;

    const fn = () => {
      try {
        window.gtag('event', 'add_payment_info', {
          value: meta?.value,
          currency: meta?.currency,
          payment_type: meta?.payment_type,
          items,
        });

        if (this.debugMode) {
          console.log('GA add_payment_info:', items.length, meta);
        }
      } catch (error) {
        console.error('Failed to track add_payment_info:', error);
      }
    };

    if (!this.isInitialized) {
      if (this.isConsentGiven) {
        this.eventQueue.push(fn);
        this.initialize();
      }
      return;
    }

    fn();
  }

  /**
   * Track adding shipping info during checkout
   */
  trackAddShippingInfo(
    items: GAEcommerceItem[],
    meta?: { value?: number; currency?: string; shipping_tier?: string },
  ): void {
    if (!this.isEnabled) return;

    const fn = () => {
      try {
        window.gtag('event', 'add_shipping_info', {
          value: meta?.value,
          currency: meta?.currency,
          shipping_tier: meta?.shipping_tier,
          items,
        });

        if (this.debugMode) {
          console.log('GA add_shipping_info:', items.length, meta);
        }
      } catch (error) {
        console.error('Failed to track add_shipping_info:', error);
      }
    };

    if (!this.isInitialized) {
      if (this.isConsentGiven) {
        this.eventQueue.push(fn);
        this.initialize();
      }
      return;
    }

    fn();
  }

  /**
   * Track refund with transaction_id for deduping
   */
  trackRefund(refund: {
    transaction_id: string;
    value?: number;
    currency?: string;
    items?: GAEcommerceItem[];
  }): void {
    if (!this.isEnabled) return;

    const fn = () => {
      try {
        window.gtag('event', 'refund', {
          transaction_id: refund.transaction_id,
          value: refund.value,
          currency: refund.currency,
          items: refund.items,
          send_to: this.measurementId!,
        });

        if (this.debugMode) {
          console.log('GA refund:', refund);
        }
      } catch (error) {
        console.error('Failed to track refund:', error);
      }
    };

    if (!this.isInitialized) {
      if (this.isConsentGiven) {
        this.eventQueue.push(fn);
        this.initialize();
      }
      return;
    }

    fn();
  }

  /**
   * Track promotion views and selections
   */
  trackViewPromotion(promo: {
    promotion_id?: string;
    promotion_name?: string;
    creative_name?: string;
    creative_slot?: string;
    location_id?: string;
  }): void {
    if (!this.isEnabled) return;

    const fn = () => {
      try {
        window.gtag('event', 'view_promotion', {
          ...promo,
        });

        if (this.debugMode) {
          console.log('GA view_promotion:', promo);
        }
      } catch (error) {
        console.error('Failed to track view_promotion:', error);
      }
    };

    if (!this.isInitialized) {
      if (this.isConsentGiven) {
        this.eventQueue.push(fn);
        this.initialize();
      }
      return;
    }

    fn();
  }

  trackSelectPromotion(promo: {
    promotion_id?: string;
    promotion_name?: string;
    creative_name?: string;
    creative_slot?: string;
    location_id?: string;
  }): void {
    if (!this.isEnabled) return;

    const fn = () => {
      try {
        window.gtag('event', 'select_promotion', {
          ...promo,
        });

        if (this.debugMode) {
          console.log('GA select_promotion:', promo);
        }
      } catch (error) {
        console.error('Failed to track select_promotion:', error);
      }
    };

    if (!this.isInitialized) {
      if (this.isConsentGiven) {
        this.eventQueue.push(fn);
        this.initialize();
      }
      return;
    }

    fn();
  }

  /**
   * Track user engagement
   */
  trackEngagement(action: string, category: string = 'engagement'): void {
    this.trackEvent({
      action,
      category,
    });
  }

  /**
   * Set user ID for better user tracking across sessions
   */
  setUserId(userId: string): void {
    if (!this.isEnabled) return;

    const setUserIdFn = () => {
      try {
        const config: GAUserConfig = {
          user_id: userId,
        };

        // Production-specific user properties
        if (import.meta.env.PROD) {
          config.custom_parameter_1 = 'registered_user';
          config.custom_parameter_2 = sessionStorage.getItem('ga_session_id') || 'unknown';
        }

        window.gtag('config', this.measurementId!, config);

        // Set user properties for better segmentation
        window.gtag('event', 'user_engagement', {
          engagement_time_msec: 1000,
          user_id: userId,
        });

        if (this.debugMode) {
          console.log('GA User ID set:', userId);
        }
      } catch (error) {
        console.error('Failed to set user ID:', error);
      }
    };

    if (!this.isInitialized) {
      if (this.isConsentGiven) {
        this.eventQueue.push(setUserIdFn);
        this.initialize();
      }
      return;
    }

    setUserIdFn();
  }

  /**
   * Set user properties with queuing support
   */
  setUserProperties(properties: Record<string, string | number | boolean>): void {
    if (!this.isEnabled) return;

    const setPropertiesFn = () => {
      try {
        window.gtag('set', properties);

        if (this.debugMode) {
          console.log('GA User Properties:', properties);
        }
      } catch (error) {
        console.error('Failed to set user properties:', error);
      }
    };

    if (!this.isInitialized) {
      if (this.isConsentGiven) {
        this.eventQueue.push(setPropertiesFn);
        this.initialize();
      }
      return;
    }

    setPropertiesFn();
  }

  /**
   * Enable/disable analytics
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;

    if (this.debugMode) {
      console.log(`Google Analytics ${enabled ? 'enabled' : 'disabled'}`);
    }
  }

  /**
   * Check if analytics is initialized
   */
  isReady(): boolean {
    return this.isInitialized && this.isEnabled;
  }

  /**
   * Get measurement ID
   */
  getMeasurementId(): string | null {
    return this.measurementId;
  }
}

// Export singleton instance
export const analytics = new GoogleAnalyticsService();
export default analytics;
